import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Flex, InputNumber, Space, Steps, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { ScanInput } from "@/components/ScanInput";
import { useI18n } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import type { ScanResult } from "@/lib/scanSound";
import { useWmsWarehouse, type Task } from "./common";

type Line = NonNullable<Task["lines"]>[number];

/**
 * Picking by scanning (Plan §6.9), line by line in walking order: scan the
 * bin, scan the item (or each serial), confirm the quantity. With a tote
 * (single, batch) everything goes into it; with a cart (cluster) each pick
 * ends by scanning the slot of the order it is for, which saves it. A
 * warehouse can require the tote or cart, scanned before the first line.
 */
export function PickPanel({ task, lineId, onLine }: { task: Task; lineId: string | null; onLine: (id: string | null) => void }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { current: warehouse } = useWmsWarehouse();
  const lines = task.lines ?? [];
  // Totes and carts: wave tasks only.
  const [wantContainer, setWantContainer] = useState(false);
  const canContain = task.waveId != null;
  // Cluster: one loose tote per order (#1, #2, ...), or a cart's slots.
  const totes = task.totes ?? [];
  const totesReady = (task.slots ?? 0) > 0 && totes.length >= (task.slots ?? 0);
  const askContainer = canContain && !task.container && !totesReady && (!!warehouse?.containerRequired || wantContainer || totes.length > 0);
  const cart = task.container?.kind === "cart" || totesReady;
  /** What to scan for an order's slot: its tote, or the cart's slot label (CART-01-05). */
  const slotCode = (slot: number | null | undefined) =>
    slot == null
      ? null
      : (totes.find((x) => x.slot === slot)?.code ??
        (task.container?.kind === "cart" ? `${task.container.code}-${String(slot).padStart(2, "0")}` : null));
  const open = lines.filter((l) => l.doneQty < l.quantity);
  const line = lines.find((l) => l.id === lineId && l.doneQty < l.quantity) ?? open[0] ?? null;
  const [binOk, setBinOk] = useState<string | null>(null);
  const [itemOk, setItemOk] = useState<string | null>(null);
  const [qty, setQty] = useState<number | null>(null);
  const [serials, setSerials] = useState<string[]>([]);
  const rest = line ? line.quantity - line.doneQty : 0;
  const atBin = !!line && binOk === line.id;
  const hasItem = !!line && itemOk === line.id;

  const reset = () => {
    setBinOk(null);
    setItemOk(null);
    setQty(null);
    setSerials([]);
  };
  const done = (res: Task) => {
    queryClient.setQueryData(["tasks", "detail", task.id], res);
    for (const k of ["tasks", "waves", "inventory", "locations"]) void queryClient.invalidateQueries({ queryKey: [k] });
    reset();
    onLine(null);
    if (res.status === "done") void message.success(t("wms.task.allDone"));
  };
  const pick = useMutation({
    mutationFn: (slot?: { code: string; quantity: number }) =>
      unwrap(
        api.POST("/v1/tasks/{taskID}/lines/{lineID}/pick", {
          params: { path: { taskID: task.id, lineID: line!.id } },
          body: {
            fromCode: line!.fromCode,
            ...(line!.tracking === "serial" ? { serials } : { quantity: slot?.quantity ?? qty ?? rest }),
            ...(slot ? { slotCode: slot.code } : {}),
          },
        }),
      ),
    onSuccess: (res) => {
      void message.success(t("wms.pick.saved", { code: line!.skuCode }));
      done(res);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const bind = useMutation({
    // Cluster: a tote goes to the next order (#1, #2, ...); a cart's label binds the cart.
    mutationFn: (code: string) =>
      task.method === "cluster" && !/^CART-/i.test(code)
        ? unwrap(api.POST("/v1/tasks/{taskID}/totes", { params: { path: { taskID: task.id } }, body: { code } }))
        : unwrap(api.POST("/v1/tasks/{taskID}/container", { params: { path: { taskID: task.id } }, body: { code } })),
    onSuccess: (res, code) => {
      queryClient.setQueryData(["tasks", "detail", task.id], res);
      setWantContainer(false);
      void message.success(t("wms.pick.containerBound", { code: res.container?.code ?? code.toUpperCase() }));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const short = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/tasks/{taskID}/lines/{lineID}/short", { params: { path: { taskID: task.id, lineID: line!.id } } })),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });

  if (!line) return <Alert type="success" showIcon title={t("wms.task.allDone")} />;

  const isItem = (code: string) => code.toUpperCase() === line.skuCode.toUpperCase() || code === line.barcode;
  // A cart slot (CART-01-05): the order in that slot takes what it still
  // needs of this line, up to the quantity counted.
  const toSlot = (code: string): ScanResult => {
    const o = (line.orders ?? []).find((x) => slotCode(x.slot)?.toUpperCase() === code.toUpperCase() && x.picked < x.quantity);
    if (!o) {
      void message.warning(t("wms.pick.wrongSlot", { code }));
      return false;
    }
    return pick.mutateAsync({ code, quantity: Math.min(qty ?? rest, o.quantity - o.picked) });
  };
  const scan = (code: string): ScanResult => {
    if (askContainer) return bind.mutateAsync(code);
    const up = code.toUpperCase();
    if (!atBin) {
      if (up === line.fromCode.toUpperCase()) {
        setBinOk(line.id);
        return true;
      }
      void message.warning(t("wms.pick.wrongBin", { code: line.fromCode }));
      return false;
    }
    if (line.tracking === "serial") {
      if (isItem(code)) {
        setItemOk(line.id);
        return true;
      }
      if (cart && hasItem && serials.length > 0) return toSlot(code);
      setItemOk(line.id);
      setSerials((s) => (s.includes(code) || s.length >= rest ? s : [...s, code]));
      return true;
    }
    if (isItem(code)) {
      setItemOk(line.id);
      setQty((q) => (hasItem ? Math.min((q ?? 0) + 1, rest) : rest));
      return true;
    }
    if (cart && hasItem) return toSlot(code);
    void message.warning(t("wms.pick.wrongItem", { code: line.skuCode }));
    return false;
  };
  const ready = hasItem && (line.tracking === "serial" ? serials.length > 0 : (qty ?? 0) > 0);
  const step = !atBin ? 0 : !hasItem ? 1 : cart ? 3 : 2;

  return (
    <Card size="small" title={t("wms.pick.title", { n: lines.length - open.length + 1, total: lines.length })}>
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {askContainer ? (
          <Alert
            type="info"
            showIcon
            title={
              task.method === "cluster"
                ? t("wms.pick.scanCart", { n: totes.length + 1, total: task.slots ?? 0 })
                : t("wms.pick.scanTote")
            }
            description={totes.length > 0 ? totes.map((x) => `#${x.slot} ${x.code}`).join(" · ") : undefined}
          />
        ) : (
          <Steps
            size="small"
            current={step}
            items={[
              { title: t("wms.pick.stepBin") },
              { title: t("wms.pick.stepItem") },
              { title: t("wms.pick.stepQty") },
              ...(cart ? [{ title: t("wms.pick.stepSlot") }] : []),
            ]}
          />
        )}
        <ScanInput
          onScan={scan}
          disabled={bind.isPending}
          placeholder={
            askContainer
              ? t(task.method === "cluster" ? "wms.pick.scanCartPlaceholder" : "wms.pick.scanTotePlaceholder")
              : cart && hasItem
                ? t("wms.pick.scanSlotPlaceholder")
                : undefined
          }
        />
        {task.container ? (
          <Typography.Text type="secondary">
            {t(cart ? "wms.pick.intoCart" : "wms.pick.intoTote")}{" "}
            <Tag className="z-num" color="processing">
              {task.container.code}
            </Tag>
          </Typography.Text>
        ) : (
          canContain &&
          !askContainer && (
            <Button size="small" type="link" style={{ padding: 0, alignSelf: "flex-start" }} onClick={() => setWantContainer(true)}>
              {t(task.method === "cluster" ? "wms.pick.useCart" : "wms.pick.useTote")}
            </Button>
          )
        )}
        <Card size="small" type="inner">
          <Space orientation="vertical" size={6} style={{ width: "100%" }}>
            <Flex justify="space-between" align="center">
              <Typography.Text type="secondary">{t("wms.pick.goTo")}</Typography.Text>
              <Tag color={atBin ? "success" : "processing"} className="z-num" style={{ fontSize: 18, padding: "4px 10px" }}>
                {line.fromCode}
              </Tag>
            </Flex>
            <SkuThumb skuId={line.skuId} size={120} />
            <Typography.Text strong className="z-num">
              {line.skuCode}
            </Typography.Text>
            <Typography.Text>{line.skuName}</Typography.Text>
            {line.lotNo && (
              <Typography.Text type="secondary">
                {t("inventory.bin.lot")} {line.lotNo}
                {line.expiresOn ? ` · ${line.expiresOn}` : ""}
              </Typography.Text>
            )}
            <Typography.Text>
              {t("wms.pick.take")}{" "}
              <Typography.Text strong className="z-num" style={{ fontSize: 20 }}>
                {rest}
              </Typography.Text>
            </Typography.Text>
            {task.method === "cluster" && (line.orders?.length ?? 0) > 0 && (
              <Space orientation="vertical" size={2}>
                <Typography.Text type="secondary">{t("wms.pick.slots")}</Typography.Text>
                <Space wrap>
                  {line.orders!.map((o) => (
                    <Tag key={o.orderId} color={o.picked >= o.quantity ? "success" : "purple"} className="z-num">
                      #{o.slot ?? "?"} {slotCode(o.slot) ?? ""}: {o.quantity - o.picked}
                    </Tag>
                  ))}
                </Space>
              </Space>
            )}
          </Space>
        </Card>
        {hasItem &&
          (line.tracking === "serial" ? (
            <Space wrap>
              <Typography.Text type="secondary">{t("wms.in.serialsHelp", { n: serials.length })}</Typography.Text>
              {serials.map((s) => (
                <Tag key={s} closable onClose={() => setSerials((x) => x.filter((y) => y !== s))} className="z-num">
                  {s}
                </Tag>
              ))}
            </Space>
          ) : (
            <InputNumber
              prefix={t("wms.in.quantity")}
              min={1}
              max={rest}
              precision={0}
              value={qty}
              onChange={setQty}
              onPressEnter={() => ready && !cart && pick.mutate(undefined)}
              style={{ width: "100%" }}
            />
          ))}
        <Flex gap={8} wrap>
          {!cart && (
            <Button type="primary" disabled={!ready || askContainer} loading={pick.isPending} onClick={() => pick.mutate(undefined)}>
              {t("wms.pick.confirm")}
            </Button>
          )}
          <Button
            danger
            disabled={!atBin}
            loading={short.isPending}
            onClick={() =>
              modal.confirm({
                title: t("wms.pick.short"),
                content: t("wms.pick.shortConfirm", { n: rest, code: line.skuCode, bin: line.fromCode }),
                okButtonProps: { danger: true },
                okText: t("wms.pick.short"),
                cancelText: t("common.cancel"),
                onOk: () => short.mutateAsync(),
              })
            }
          >
            {t("wms.pick.short")}
          </Button>
          {open.length > 1 && (
            <Button
              type="text"
              onClick={() => {
                reset();
                onLine(open[(open.indexOf(line) + 1) % open.length]!.id);
              }}
            >
              {t("wms.pick.skip")}
            </Button>
          )}
        </Flex>
      </Space>
    </Card>
  );
}

/** The line orders for a picking table: who gets what (cluster slots). */
export function LineOrders({ line, method }: { line: Line; method: string }) {
  const { t } = useI18n();
  const orders = line.orders ?? [];
  if (orders.length === 0) return <Typography.Text type="secondary">—</Typography.Text>;
  if (method !== "cluster") return <Typography.Text type="secondary">{t("wms.pick.forOrders", { n: orders.length })}</Typography.Text>;
  return (
    <Space size={4} wrap>
      {orders.map((o) => (
        <Tag key={o.orderId} className="z-num" color={o.picked >= o.quantity ? "success" : undefined}>
          {t("wms.wave.slot", { n: o.slot ?? "?" })} ×{o.quantity}
        </Tag>
      ))}
    </Space>
  );
}
